import { useRef } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useWidth } from '@hotjar/insights-common';
import { clamp } from '@hotjar/insights-common/lib/number';
import { HEATMAP_WIDTH_PER_DEVICE } from '../../constants-types/HeatmapWidthPerDevice';
import {
  CLICK_REGION_BASE_Z_INDEX,
  TOP_CLICKED_REGION_CLASS_PREFIX,
} from '../../constants-types/HeatmapLayers';
import { Layer } from '../Layer/Layer';

const TopClicksContainer = Layer.withComponent(styled.div<{ zoom: number; height: number }>`
  position: relative;
  width: 100%;

  ${({ height, zoom }) => `
    max-height: ${height}px;
    zoom: ${zoom};
  `}
`);

const TopClick = styled.div<{
  position: { left: number; top: number };
  zIndex: number;
}>`
  position: absolute;
  background: ${tokens.color.ui_background};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.m};
  padding: ${tokens.spacer.x0} ${tokens.spacer.x1};
  ${tokens.elevation(2)};
  ${({ position }) => `
    left: ${position.left}px;
    top: ${position.top}px;
  `}
  z-index: ${({ zIndex }) => zIndex};
`;

const NUMBER_OF_TOP_CLICKED_ELEMENTS = 3;

const calculateMarkPosition = (
  coords: HeatmapAPI.TopClickedElement,
  heatmapWidth: number,
  actualWidth: number,
) => {
  const zoom = clamp(actualWidth / heatmapWidth, 0, 1);

  let left = coords[1] * zoom;
  const top = coords[2] * zoom;

  if (left < 0) {
    left = 0;
  }

  return {
    left,
    top,
  };
};

function toggleClickRegion(index: number, visible: boolean) {
  const clickRegion = document.querySelector<HTMLElement>(
    `.${TOP_CLICKED_REGION_CLASS_PREFIX}${index + 1}`,
  );
  if (clickRegion) {
    clickRegion.style.cssText = visible ? 'opacity: 1' : '';
  }
}

export const TopClicksLayer = ({
  topClickedElements,
  selectedDeviceType,
  width,
  height,
  clickRegionsCount,
}: {
  topClickedElements: HeatmapAPI.TopClickedElement[];
  selectedDeviceType: HeatmapDeviceTypes;
  width: number;
  height: number;
  clickRegionsCount: number;
}): JSX.Element => {
  const zoom = HEATMAP_WIDTH_PER_DEVICE[selectedDeviceType] / width;

  const ref = useRef<HTMLDivElement>(null);
  const [actualWidth] = useWidth(ref, width);

  return (
    <TopClicksContainer ref={ref} zoom={zoom} height={height}>
      {topClickedElements.map((topClickedElement, index) => {
        // Top clicked element gets the highest z-Index, that's why we do
        // (NUMBER_OF_TOP_CLICKED_ELEMENTS - index). This way, for instance,
        // if we have 100 click regions, the z indexes for the top 3 clicked elements would be:
        // 203, 202, 201, respectively.
        const zIndex =
          clickRegionsCount + CLICK_REGION_BASE_Z_INDEX + (NUMBER_OF_TOP_CLICKED_ELEMENTS - index);
        return (
          <TopClick
            key={topClickedElement[0]}
            position={calculateMarkPosition(topClickedElement, width, actualWidth)}
            zIndex={zIndex}
            onMouseEnter={() => {
              toggleClickRegion(index, true);
            }}
            onMouseLeave={() => {
              toggleClickRegion(index, false);
            }}
          >
            {index + 1}
          </TopClick>
        );
      })}
    </TopClicksContainer>
  );
};
